/**
 * Client-side helper for sending an inbound web message with file attachments.
 *
 * Wraps XHR (not fetch) because we need upload progress events for the UI.
 * The endpoint accepts multipart/form-data with `text`, optional `metadata`
 * (JSON string), and one or more `files` parts. On success, the agent's
 * reply streams back asynchronously over the existing WebSocket — this
 * helper resolves as soon as the server acknowledges the inbound message.
 */

import type { MessageContext } from "@shared/companion/message-context";

// Mirrors apps/runtime/src/gateway/web-message.ts (MAX_FILES_PER_MESSAGE)
// and apps/runtime/src/media/store.ts (DEFAULT_MAX_BYTES). The server is the
// authority — these values are only used for client-side gating to surface
// nicer error messages before kicking off an upload that would be rejected.
const MAX_FILES_PER_MESSAGE = 5;
const MAX_BYTES_PER_FILE = 25 * 1024 * 1024;

export interface UploadProgress {
  /** Bytes transferred so far (across all files in this message). */
  readonly loaded: number;
  /** Total bytes expected. May be 0 before the request starts. */
  readonly total: number;
}

export type SendMessageError =
  | { kind: "too_many_files"; max: number }
  | { kind: "file_too_large"; fileName: string; max: number }
  /** The browser could not read the file (see firstUnreadable). */
  | { kind: "unreadable_file"; fileName: string }
  | { kind: "rejected"; status: number; reason: string }
  | { kind: "network"; message: string };

export interface SendMessageOptions {
  readonly url: string;
  /** Auth for the gateway (lib/gateway-auth.tsx); none on the legacy path,
   *  whose `url` carries the gateway token. */
  readonly headers?: Record<string, string>;
  readonly text: string;
  readonly files: File[];
  readonly metadata?: Record<string, unknown>;
  /** Where the person was when they sent it (the browser side panel's page). */
  readonly context?: MessageContext | null;
  readonly onProgress?: (progress: UploadProgress) => void;
  /** AbortSignal to cancel the in-flight upload. */
  readonly signal?: AbortSignal;
}

const validate = (files: File[]): SendMessageError | null => {
  if (files.length > MAX_FILES_PER_MESSAGE) {
    return { kind: "too_many_files", max: MAX_FILES_PER_MESSAGE };
  }
  const oversized = files.find((f) => f.size > MAX_BYTES_PER_FILE);
  return oversized ? { kind: "file_too_large", fileName: oversized.name, max: MAX_BYTES_PER_FILE } : null;
};

/**
 * The first file the browser cannot read, or null.
 *
 * A file can be picked and still be unreadable: a cloud-only placeholder
 * (OneDrive, iCloud Drive) that will not download, or a file moved or changed
 * after it was picked. XHR then fails the request before a byte is sent and
 * reports it as a plain network error, so the person was told to "check your
 * connection" about a file on their own disk (report c8539cb8: a small .docx,
 * no request ever reached the server). Reading one byte of each file first
 * tells the two apart without reading whole files.
 */
export const firstUnreadable = async (files: readonly File[]): Promise<File | null> => {
  const unreadable = await Promise.all(
    files.map((f) => f.slice(0, 1).arrayBuffer().then(() => null, () => f)),
  );
  return unreadable.find((f) => f !== null) ?? null;
};

const rejectUnreadable = async (files: readonly File[]): Promise<void> => {
  const unreadable = await firstUnreadable(files);
  // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
  if (unreadable) return Promise.reject({ kind: "unreadable_file", fileName: unreadable.name } satisfies SendMessageError);
};

/**
 * POST a chat message + attachments to the runtime.
 *
 * Resolves on 2xx (the message has been stored — the agent's streaming reply
 * arrives over WS). Rejects with a typed {@link SendMessageError} on size
 * caps, server rejection, or transport failure.
 */
export const sendMessageWithAttachments = (opts: SendMessageOptions): Promise<void> => {
  const validationError = validate(opts.files);
  // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
  if (validationError) return Promise.reject(validationError);

  return rejectUnreadable(opts.files).then(() => new Promise<void>((resolve, reject) => {
    const form = new FormData();
    form.append("text", opts.text);
    if (opts.metadata) form.append("metadata", JSON.stringify(opts.metadata));
    if (opts.context) form.append("context", JSON.stringify(opts.context));
    opts.files.forEach((f) => { form.append("files", f, f.name); });

    const xhr = new XMLHttpRequest();
    xhr.open("POST", opts.url);
    Object.entries(opts.headers ?? {}).forEach(([name, value]) => { xhr.setRequestHeader(name, value); });

    if (opts.onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        opts.onProgress!({ loaded: e.loaded, total: e.total });
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      const reason = parseError(xhr.responseText);
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
      reject({ kind: "rejected", status: xhr.status, reason } satisfies SendMessageError);
    });
    xhr.addEventListener("error", () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
      reject({ kind: "network", message: "Upload failed" } satisfies SendMessageError);
    });
    xhr.addEventListener("abort", () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
      reject({ kind: "network", message: "Upload aborted" } satisfies SendMessageError);
    });

    if (opts.signal) {
      const onAbort = () => { xhr.abort(); };
      opts.signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.send(form);
  }));
};

const parseError = (body: string): string => {
  try {
    const parsed = JSON.parse(body);
    return typeof parsed?.error === "string" ? parsed.error : body || "unknown";
  } catch {
    return body || "unknown";
  }
};

export interface WorkspaceUploadOptions {
  /** The gateway credential (sent as a Bearer header) — lib/gateway-auth.tsx. */
  readonly token: string;
  /** Target directory, workspace-relative. "." for the workspace root. */
  readonly dir: string;
  readonly files: File[];
  readonly onProgress?: (progress: UploadProgress) => void;
  readonly signal?: AbortSignal;
}

/**
 * Upload files straight into a workspace directory via the runtime's
 * `POST /files/upload` endpoint (distinct from chat's `/web/message` — these
 * land in the tree, not in a conversation). Resolves with the written
 * workspace-relative paths (the server renames on name collision). Rejects with
 * a typed {@link SendMessageError} on size caps, server rejection, or transport
 * failure. Uses XHR for progress, mirroring {@link sendMessageWithAttachments}.
 */
export const uploadFilesToWorkspace = (opts: WorkspaceUploadOptions): Promise<string[]> => {
  const validationError = validate(opts.files);
  // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
  if (validationError) return Promise.reject(validationError);

  return rejectUnreadable(opts.files).then(() => new Promise<string[]>((resolve, reject) => {
    const form = new FormData();
    form.append("dir", opts.dir);
    opts.files.forEach((f) => { form.append("files", f, f.name); });

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/gateway/files/upload");
    xhr.setRequestHeader("Authorization", `Bearer ${opts.token}`);

    if (opts.onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        opts.onProgress!({ loaded: e.loaded, total: e.total });
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const parsed = JSON.parse(xhr.responseText);
          resolve(Array.isArray(parsed?.paths) ? parsed.paths : []);
        } catch {
          resolve([]);
        }
        return;
      }
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
      reject({ kind: "rejected", status: xhr.status, reason: parseError(xhr.responseText) } satisfies SendMessageError);
    });
    xhr.addEventListener("error", () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
      reject({ kind: "network", message: "Upload failed" } satisfies SendMessageError);
    });
    xhr.addEventListener("abort", () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- typed SendMessageError union is the documented public API; consumers narrow on .kind, not Error
      reject({ kind: "network", message: "Upload aborted" } satisfies SendMessageError);
    });

    if (opts.signal) {
      opts.signal.addEventListener("abort", () => { xhr.abort(); }, { once: true });
    }

    xhr.send(form);
  }));
};

export const UPLOAD_LIMITS = {
  maxFilesPerMessage: MAX_FILES_PER_MESSAGE,
  maxBytesPerFile: MAX_BYTES_PER_FILE,
} as const;
